import { useThemeStore } from "@stores/theme.store";
import { Select } from "antd";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { SchoolService } from "@services/school.service";
import { StudentEkskulAbsence } from "@services/dtos/school.dto";
import { useQuery } from "@tanstack/react-query";
import { Spiner } from "@components/loaders/spiner";
import { FcFinePrint } from "react-icons/fc";
moment.locale("id");

const statusColor: Record<string, string> = {
  hadir: "tw-bg-green-100 tw-text-green-700",
  "tidak hadir": "tw-bg-red-100 tw-text-red-700",
  izin: "tw-bg-yellow-100 tw-text-yellow-700",
  sakit: "tw-bg-blue-100 tw-text-blue-700",
};

export const EkskulAbsenceDataPage = () => {
  const { setHeader, setAppBar } = useThemeStore((state) => state);
  const [limit, setLimit] = useState<string>("10");

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data, isPending } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_EKSKUL_ABSENCES, nis, limit],
    queryFn: () => SchoolService.studentEkskulAbsences({ nis, limit }),
    enabled: !!nis,
  });

  useEffect(() => {
    setAppBar({ title: "Absensi Ekskul", show: true, leading: "back" });
    setHeader({ buttonBack: true, filter: false, title: "Absensi Ekskul" });
  }, [setHeader, setAppBar]);

  const onWhatsApp = (nohp: string, nama_ekskul: string, pengampu: string) => {
    const no = nohp.startsWith("0") ? "62" + nohp.slice(1) : nohp;
    const text = encodeURIComponent(
      `Assalamu'alaikum, saya ingin menanyakan mengenai ekskul ${nama_ekskul} (Pengampu: ${pengampu}).`
    );
    window.open(`https://wa.me/${no}?text=${text}`, "_blank");
  };

  const renderItem = (item: StudentEkskulAbsence, idx: number) => {
    const colorClass =
      statusColor[item.status?.toLowerCase()] ?? "tw-bg-gray-100 tw-text-gray-700";
    return (
      <div
        key={idx}
        className="tw-border-solid tw-border-x-0 tw-border-y-1 tw-border-y-other-200 tw-py-2 tw-mb-0.5"
      >
        <div className="tw-px-2 tw-flex tw-justify-between tw-items-center">
          <div className="tw-text-xs tw-font-medium tw-text-other-600">
            {item.nama_ekskul}
          </div>
          <span className={`tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full ${colorClass}`}>
            {item.status}
          </span>
        </div>

        <div className="tw-px-2 tw-flex tw-justify-between tw-items-center tw-mt-0.5">
          <div className="tw-text-sm tw-font-semibold">
            {moment(item.tanggal).format("DD MMMM YYYY")}
          </div>
          <div className="tw-text-sm tw-text-other-500">Pekan {item.pekan}</div>
        </div>

        <div className="tw-px-2 tw-text-xs tw-text-other-500 tw-mt-0.5">
          Pengampu: {item.pengampu || "-"}
        </div>

        {item.nohp ? (
          <div className="tw-px-2 tw-mt-1">
            <button
              onClick={() => onWhatsApp(item.nohp, item.nama_ekskul, item.pengampu)}
              className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-green-600 tw-font-medium tw-bg-green-50 tw-border tw-border-solid tw-border-green-200 tw-rounded-full tw-px-3 tw-py-1"
            >
              <svg viewBox="0 0 24 24" className="tw-w-3.5 tw-h-3.5 tw-fill-green-600">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.554 4.122 1.522 5.853L.057 23.996l6.304-1.654A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.8 9.8 0 01-4.99-1.364l-.358-.213-3.714.974.992-3.62-.233-.372A9.789 9.789 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z" />
              </svg>
              Hubungi Pengampu
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="tw-px-4 tw-pb-20">
        <div className="tw-bg-other-100 tw-rounded-lg card-body">
          <div className="tw-text-xs tw-mb-1">Filter Data</div>
          <Select
            className="tw-border-other-0 tw-text-base"
            placeholder="Pilih Jumlah Ditampilkan"
            defaultValue={limit}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setLimit(e ?? "10")}
            options={[
              { value: "10", label: "10" },
              { value: "25", label: "25" },
              { value: "50", label: "50" },
              { value: "100", label: "100" },
            ]}
          />
          <p className="tw-mt-3 tw-text-sm">
            Data yang ditampilkan {limit} data terakhir
          </p>
        </div>

        <div className="tw-bg-other-100 tw-rounded-lg card-body tw-mt-3">
          {isPending ? (
            <div className="tw-text-center">
              <Spiner />
            </div>
          ) : data?.data != null && data.data.length > 0 ? (
            <>
              {data.data.map((item, idx) => renderItem(item, idx))}
            </>
          ) : (
            <div className="tw-p-8 tw-text-center">
              <FcFinePrint size={50} />
              <div>Data tidak ditemukan</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
